import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  InputLabel,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import Title from '@Components/Title';
import FilterSearch from '@Components/Filter/Search';
import { SBTable } from '@Components/Table/Table';
import { TableHeadCell, TableRowCell } from '@Components/Table/TableCell';
import { Vendor } from '../constants';
import { CircularProgress } from '@mui/material';

export const SearchFilterList = [{ label: 'Carrier', value: 'carrier' }];

interface VendorSelectionModalProps {
  open: boolean;
  carrierLoading?: boolean;
  vendors: Vendor[];
  onVendorSelect: (vendor: Vendor) => void;
  onClose?: () => void;
  searchTerm?: string;
}

const VendorSelectionModal: React.FC<VendorSelectionModalProps> = ({
  open,
  vendors,
  onVendorSelect,
  onClose,
  searchTerm,
  carrierLoading,
}) => {
  const [searchValue, setSearchValue] = useState(searchTerm || '');
  const [filterSelectValue, setFilterSelectValue] = useState('carrier');

  useEffect(() => {
    if (searchTerm !== undefined) setSearchValue(searchTerm);
  }, [searchTerm]);

  const filteredVendors = searchValue
    ? vendors.filter(v => v.name.toLowerCase().includes(searchValue.toLowerCase()))
    : vendors;

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: '90vh',
          padding: '48px',

          // 🧊 lighter but more solid glass
          background: 'rgba(255, 255, 255, 0.9)', // ⬅️ increased opacity

          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',

          // 🔵 Rounded corners
          borderRadius: '22px',

          // 🧼 subtle border
          border: '1px solid rgba(255, 255, 255, 0.18)',

          // 🌑 soft depth shadow
          boxShadow: `
        0 12px 40px rgba(0, 0, 0, 0.22),
        inset 0 1px 0 rgba(255, 255, 255, 0.08)
      `,

          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ paddingBottom: 1, position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Title title="Select Vendor" subTitle="Choose a vendor to view detailed transaction history." removeBorder />
          {onClose && (
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>{' '}
        <InputLabel sx={{ mb: 1 }} className="sb-text-gray">
          Enter Carrier name to search.
        </InputLabel>
        {/* Search */}
        <FilterSearch
          onSelectChange={value => setFilterSelectValue(value)}
          list={SearchFilterList}
          selectValue={filterSelectValue}
          textValue={searchValue}
          onTextChange={e => setSearchValue(e.target.value)}
          onTextSearch={() => console.info('Search for:', filterSelectValue, searchValue)}
          onTextClear={() => setSearchValue('')}
        />
      </Box>
      {carrierLoading ? (
        <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
          <CircularProgress color="primary" />
        </Grid>
      ) : (
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          {/* Vendor Table */}
          <TableContainer
            id="vendorScroll"
            sx={{
              mt: 3,
              maxHeight: 'calc(100vh - 300px)',
              overflowY: 'auto',
              borderRadius: 1,
            }}
          >
            <SBTable>
              <TableHead>
                <TableRow>
                  <TableHeadCell is_light="1" sx={{ minWidth: '150px' }}>
                    S.No
                  </TableHeadCell>
                  <TableHeadCell is_light="1" sx={{ minWidth: '180px', width: '250px' }}>
                    Carrier Name
                  </TableHeadCell>
                  <TableHeadCell is_light="1" sx={{ minWidth: '180px', width: '250px' }}>
                    No. of Paid Invoices
                  </TableHeadCell>
                  <TableHeadCell is_light="1" sx={{ minWidth: '180px', width: '250px' }}>
                    No. of Unpaid Invoices
                  </TableHeadCell>
                  {/* <TableHeadCell is_light="1" sx={{ minWidth: '180px', width: '210px' }}>
                  Credit Note Balance
                </TableHeadCell> */}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredVendors.map((vendor, index) => (
                  <TableRow key={vendor.id} hover className="cursor-pointer" onClick={() => onVendorSelect(vendor)}>
                    <TableRowCell className="md-text" align="center">
                      {index + 1}
                    </TableRowCell>
                    <TableRowCell className="md-text" align="center">
                      {vendor.name}
                    </TableRowCell>
                    <TableRowCell className="md-text" align="center">
                      {vendor.paidInvoices ?? 0}
                    </TableRowCell>
                    <TableRowCell className="md-text" align="center">
                      {vendor.unpaidInvoices ?? 0}
                    </TableRowCell>
                    {/* <TableRowCell className="md-text" align="center">
                    ${vendor.cnBalance?.toLocaleString() ?? 0}
                  </TableRowCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </SBTable>

            {!filteredVendors.length && (
              <Grid container justifyContent="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No vendors found</Typography>
              </Grid>
            )}
          </TableContainer>

          {/* Optional Pagination (if needed later) */}
          {/* <Grid container justifyContent="flex-end">
          <TablePagination
            component="div"
            count={filteredVendors.length}
            page={0}
            rowsPerPage={10}
            onPageChange={() => {}}
            onRowsPerPageChange={() => {}}
          />
        </Grid> */}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default VendorSelectionModal;
